import { useAgGridDefaults } from "@/lib/ag-grid-defaults";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWorkspacePage } from "@/components/workspace/use-workspace-page";
import {
	useGetForecast,
	useListDemandSignals,
	useListPlannedActions,
	useSimulate,
	useTriggerReplan,
} from "@/hooks/use-manufacturing";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AgGridReact } from "@ag-grid-community/react";
import type { ColDef } from "@ag-grid-community/core";
import { createFileRoute } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Area,
	AreaChart,
	CartesianGrid,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";

export const Route = createFileRoute("/manufacturing/products/$id")({
	component: ProductDetailPage,
});

function ProductDetailPage() {
	const { id: productId } = Route.useParams();
	const { t } = useTranslation("manufacturing");
	const gridDefaults = useAgGridDefaults();
	const replan = useTriggerReplan();
	const simulate = useSimulate();

	// Load data for this product
	const { data: demands } = useListDemandSignals({ productId });
	const { data: actions } = useListPlannedActions({ productId });
	const { data: forecast } = useGetForecast({
		productId,
		periods: 6,
	});

	const demandSignals = demands?.signals ?? [];
	const plannedActions = actions?.actions ?? [];

	const [searchText, setSearchText] = useState("");

	const statusBar = useMemo(
		() => [
			{
				id: "product",
				content: `Товар: ${productId.slice(0, 8)}`,
				align: "left" as const,
			},
			{
				id: "signals",
				content: `${demandSignals.length} сигналов • ${plannedActions.length} действий`,
				align: "right" as const,
			},
		],
		[productId, demandSignals.length, plannedActions.length],
	);

	useWorkspacePage({ statusBar });

	// Demand signals grid columns
	const demandColumns = useMemo<ColDef[]>(
		() => [
			{
				field: "priority",
				headerName: t("field_priority"),
				width: 90,
			},
			{
				field: "source",
				headerName: t("field_source"),
				width: 100,
			},
			{
				field: "qty",
				headerName: t("field_qty"),
				width: 90,
				type: "numericColumn",
				valueFormatter: (p: { value: number }) =>
					Number(p.value).toLocaleString("ru-RU"),
			},
			{
				field: "needDate",
				headerName: t("field_need_date"),
				width: 100,
			},
			{
				field: "probability",
				headerName: t("field_probability"),
				width: 90,
				valueFormatter: (p: { value: number }) =>
					`${(p.value * 100).toFixed(0)}%`,
			},
			{
				field: "status",
				headerName: t("field_status"),
				width: 90,
			},
		],
		[t],
	);

	// Planned actions grid columns
	const actionColumns = useMemo<ColDef[]>(
		() => [
			{
				field: "actionType",
				headerName: t("field_action_type"),
				width: 100,
				valueFormatter: (p: { value: string }) =>
					t(`type_${p.value}`, { defaultValue: p.value }),
			},
			{
				field: "qty",
				headerName: t("field_qty"),
				width: 90,
				type: "numericColumn",
				valueFormatter: (p: { value: number }) =>
					Number(p.value).toLocaleString("ru-RU"),
			},
			{
				field: "targetDate",
				headerName: t("field_target_date"),
				width: 100,
			},
			{
				field: "urgency",
				headerName: t("field_urgency"),
				width: 90,
			},
			{
				field: "status",
				headerName: t("field_status"),
				width: 90,
			},
			{
				field: "confidence",
				headerName: t("field_confidence"),
				width: 80,
				valueFormatter: (p: { value: number }) =>
					`${(p.value * 100).toFixed(0)}%`,
			},
		],
		[t],
	);

	// Forecast chart data
	const forecastChartData = useMemo(() => {
		if (!forecast?.points) return [];
		return forecast.points.map((p) => ({
			period: p.period,
			forecast: p.forecast,
			lower: p.lower,
			upper: p.upper,
		}));
	}, [forecast]);

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						value={searchText}
						onChange={(e) => setSearchText(e.target.value)}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						simulate.mutate(
							{
								productId,
								demandMultiplier: 1.0,
								leadTimeMultiplier: 1.0,
								supplyDisruptionPct: 0.0,
								horizonDays: 90,
								monteCarloSamples: 1000,
							},
							{
								onSuccess: (resp) => {
									toast.success(
										`SL=${(resp.serviceLevel * 100).toFixed(0)}%, ` +
											`дефицит=${(resp.stockoutProbability * 100).toFixed(0)}%`,
									);
								},
							},
						);
					}}
				>
					{t("action_simulate")}
				</Button>
				<Button
					size="sm"
					className="h-9"
					onClick={() => replan.mutate({ productId, warehouseId: "" })}
				>
					{t("action_replan")}
				</Button>
			</div>
			<Tabs defaultValue="overview" className="flex h-full flex-col">
				<TabsList className="mx-3 mt-2 w-fit">
					<TabsTrigger value="overview">Обзор</TabsTrigger>
					<TabsTrigger value="demand">{t("demand_signals")}</TabsTrigger>
					<TabsTrigger value="actions">{t("planned_actions")}</TabsTrigger>
					<TabsTrigger value="forecast">{t("forecast")}</TabsTrigger>
				</TabsList>

				{/* Overview Tab */}
				<TabsContent value="overview" className="flex-1 overflow-y-auto p-3">
					<div className="grid grid-cols-2 gap-3">
						<div className="rounded-md border bg-background p-3">
							<h4 className="mb-2 text-xs font-semibold text-muted-foreground uppercase">
								{t("demand_signals")}
							</h4>
							<div className="text-2xl font-semibold tabular-nums">
								{demandSignals.length}
							</div>
							<div className="mt-1 text-xs text-muted-foreground">
								{demandSignals
									.filter((s) => s.status === "active")
									.length}{" "}
								{t("status_active").toLowerCase()}
							</div>
						</div>
						<div className="rounded-md border bg-background p-3">
							<h4 className="mb-2 text-xs font-semibold text-muted-foreground uppercase">
								{t("planned_actions")}
							</h4>
							<div className="text-2xl font-semibold tabular-nums">
								{plannedActions.length}
							</div>
							<div className="mt-1 text-xs text-muted-foreground">
								{plannedActions.filter((a) => a.status === "planned").length}{" "}
								{t("status_planned").toLowerCase()}
							</div>
						</div>
					</div>

					{/* Forecast preview */}
					{forecastChartData.length > 0 && (
						<div className="mt-3 rounded-md border bg-background p-3">
							<h4 className="mb-2 text-xs font-semibold text-muted-foreground uppercase">
								{t("forecast_title")}
							</h4>
							<ResponsiveContainer width="100%" height={180}>
								<AreaChart data={forecastChartData}>
									<CartesianGrid
										strokeDasharray="3 3"
										className="stroke-border"
									/>
									<XAxis
										dataKey="period"
										tick={{ fontSize: 10 }}
										className="fill-muted-foreground"
									/>
									<YAxis
										tick={{ fontSize: 10 }}
										className="fill-muted-foreground"
									/>
									<Tooltip />
									<Area
										type="monotone"
										dataKey="upper"
										stroke="none"
										fill="#6366f1"
										fillOpacity={0.08}
									/>
									<Area
										type="monotone"
										dataKey="lower"
										stroke="none"
										fill="#ffffff"
										fillOpacity={1}
									/>
									<Line
										type="monotone"
										dataKey="forecast"
										stroke="#6366f1"
										strokeWidth={2}
										dot={false}
									/>
								</AreaChart>
							</ResponsiveContainer>
							{forecast && (
								<div className="mt-2 flex gap-4 text-[11px] text-muted-foreground">
									<span>
										{t("forecast_model")}: {forecast.modelUsed}
									</span>
									<span>
										{t("forecast_pattern")}: {forecast.pattern}
									</span>
								</div>
							)}
						</div>
					)}
				</TabsContent>

				{/* Demand Signals Tab */}
				<TabsContent value="demand" className="flex-1">
					<div className="h-full ag-theme-quartz">
						<AgGridReact
							{...gridDefaults}
							columnDefs={demandColumns}
							rowData={demandSignals as unknown[]}
							getRowId={(p) => p.data?.id}
						/>
					</div>
				</TabsContent>

				{/* Planned Actions Tab */}
				<TabsContent value="actions" className="flex-1">
					<div className="h-full ag-theme-quartz">
						<AgGridReact
							{...gridDefaults}
							columnDefs={actionColumns}
							rowData={plannedActions as unknown[]}
							getRowId={(p) => p.data?.id}
						/>
					</div>
				</TabsContent>

				{/* Forecast Tab */}
				<TabsContent value="forecast" className="flex-1 overflow-y-auto p-3">
					{forecastChartData.length > 0 ? (
						<div>
							<ResponsiveContainer width="100%" height={300}>
								<AreaChart data={forecastChartData}>
									<CartesianGrid
										strokeDasharray="3 3"
										className="stroke-border"
									/>
									<XAxis
										dataKey="period"
										tick={{ fontSize: 11 }}
										className="fill-muted-foreground"
									/>
									<YAxis
										tick={{ fontSize: 11 }}
										className="fill-muted-foreground"
									/>
									<Tooltip />
									<Area
										type="monotone"
										dataKey="upper"
										stroke="none"
										fill="#6366f1"
										fillOpacity={0.1}
										name="P90"
									/>
									<Area
										type="monotone"
										dataKey="lower"
										stroke="none"
										fill="#ffffff"
										fillOpacity={1}
										name="P10"
									/>
									<Line
										type="monotone"
										dataKey="forecast"
										stroke="#6366f1"
										strokeWidth={2}
										dot
										name={t("forecast_title")}
									/>
								</AreaChart>
							</ResponsiveContainer>
							{forecast && (
								<div className="mt-4 text-xs text-muted-foreground">
									<p>
										{t("forecast_model")}: <strong>{forecast.modelUsed}</strong>
									</p>
									<p>
										{t("forecast_pattern")}: <strong>{forecast.pattern}</strong>
									</p>
								</div>
							)}
						</div>
					) : (
						<div className="flex h-full items-center justify-center text-xs text-muted-foreground">
							Недостаточно данных для прогноза
						</div>
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
